import { useCallback, useEffect, useState } from "react";

import {
  generateClientId,
  type OfflineAnswer,
  type OfflineCompletion,
  offlineDb,
  type OfflinePhoto,
} from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

type AnswerValue = "yes" | "no" | "na" | number | string;

export interface InitialAnswerFile {
  id: string;
  fileName: string;
  mimeType?: string;
  downloadUrl: string;
  uploadStatus: string;
}

interface AnsweredByUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

interface InitialAnswer {
  questionId: string;
  answerValue: string | number | boolean | string[] | null;
  notes?: string;
  files?: InitialAnswerFile[];
  answeredByUser?: AnsweredByUser;
}

// Combined photo type that can be either local (with blob) or server (with downloadUrl)
export interface DisplayPhoto {
  id: string;
  fileName: string;
  mimeType: string;
  syncState: "pending" | "synced" | "error";
  // Local photos have blob, server photos have downloadUrl
  blob?: Blob;
  downloadUrl?: string;
  // Server file status (PENDING, COMPLETED, FAILED)
  uploadStatus?: string;
  // For local photos: the server-side file ID (used to deduplicate with server files)
  serverId?: string;
}

export interface OfflineChecklistState {
  completion: OfflineCompletion | null;
  answers: Map<string, OfflineAnswer>;
  photos: Map<string, DisplayPhoto[]>;
  syncStatus: SyncStatus;
}

export function useOfflineChecklist(
  instanceId: string,
  nurseryId: string,
  practitionerId: string,
  initialAnswers?: InitialAnswer[],
) {
  const [state, setState] = useState<OfflineChecklistState>({
    completion: null,
    answers: new Map(),
    photos: new Map(),
    syncStatus: {
      pendingCount: 0,
      errorCount: 0,
      isSyncing: false,
      isOnline: navigator.onLine,
    },
  });

  // Load existing offline data and merge with initial answers from server
  useEffect(() => {
    async function loadOfflineData() {
      // Find existing completion for this instance
      const completion = await offlineDb.completions
        .filter(
          (c) =>
            c.instanceId === instanceId && c.practitionerId === practitionerId,
        )
        .first();

      const answersMap = new Map<string, OfflineAnswer>();
      const photosMap = new Map<string, DisplayPhoto[]>();

      if (completion) {
        // Load answers from IndexedDB
        const answersArray = await offlineDb.answers
          .filter((a) => a.completionId === completion.id)
          .toArray();

        answersArray.forEach((a) => answersMap.set(a.itemId, a));

        // Load local photos
        for (const answer of answersArray) {
          const localPhotos = await offlineDb.photos
            .filter((p) => p.answerId === answer.id)
            .toArray();
          if (localPhotos.length > 0) {
            // Convert OfflinePhoto to DisplayPhoto
            // Include serverId so we can deduplicate with server files
            // Convert ArrayBuffer back to Blob for display
            const displayPhotos: DisplayPhoto[] = localPhotos.map((p) => ({
              id: p.id,
              fileName: p.fileName,
              mimeType: p.mimeType,
              syncState: p.syncState,
              blob: new Blob([p.data], { type: p.mimeType }),
              serverId: p.serverId,
            }));
            photosMap.set(answer.itemId, displayPhotos);
          }
        }
      }

      // Merge with initial answers from server (server data fills gaps)
      if (initialAnswers && initialAnswers.length > 0) {
        for (const serverAnswer of initialAnswers) {
          // Only use server answer if we don't have a local answer
          if (!answersMap.has(serverAnswer.questionId)) {
            // Convert server answer to OfflineAnswer format for display
            // These are "virtual" answers - not stored in IndexedDB, just for display
            const answerValue = convertServerAnswerValue(serverAnswer.answerValue);
            if (answerValue) {
              answersMap.set(serverAnswer.questionId, {
                id: `server-${serverAnswer.questionId}`,
                completionId: completion?.id || "",
                itemId: serverAnswer.questionId,
                answer: answerValue,
                notes: serverAnswer.notes || null,
                syncState: "synced", // Already on server
              });
            }
          }

          // Merge server files with local photos
          // Server files are shown if we don't have local photos for this question
          if (serverAnswer.files && serverAnswer.files.length > 0) {
            const existingPhotos = photosMap.get(serverAnswer.questionId) || [];
            // Collect both local photo IDs and their serverIds for deduplication
            const existingIds = new Set(existingPhotos.map((p) => p.id));
            const existingServerIds = new Set(
              existingPhotos.map((p) => p.serverId).filter(Boolean),
            );
            // Only add server files that don't already exist locally
            // Check both local photo ID and serverId to avoid duplicates
            const serverPhotos: DisplayPhoto[] = serverAnswer.files
              .filter(
                (f) => !existingIds.has(f.id) && !existingServerIds.has(f.id),
              )
              .map((f) => ({
                id: f.id,
                fileName: f.fileName,
                mimeType: f.mimeType || "image/jpeg",
                syncState: "synced" as const,
                downloadUrl: f.downloadUrl,
                uploadStatus: f.uploadStatus,
              }));

            if (serverPhotos.length > 0) {
              photosMap.set(serverAnswer.questionId, [
                ...existingPhotos,
                ...serverPhotos,
              ]);
            }
          }
        }
      }

      setState((prev) => ({
        ...prev,
        completion: completion || null,
        answers: answersMap,
        photos: photosMap,
      }));

      // Get sync status
      const syncStatus = await syncService.getStatus();
      setState((prev) => ({ ...prev, syncStatus }));
    }

    // Helper to convert server answer value to local format
    function convertServerAnswerValue(
      value: string | number | boolean | string[] | null,
    ): AnswerValue | null {
      if (typeof value === "string") {
        const lower = value.toLowerCase();
        if (lower === "yes") return "yes";
        if (lower === "no") return "no";
        if (lower === "na" || lower === "n/a") return "na";
        return value;
      }
      if (typeof value === "number") return value;
      return null;
    }

    loadOfflineData();

    // Subscribe to sync status updates
    const unsubscribe = syncService.subscribe((syncStatus) => {
      setState((prev) => ({ ...prev, syncStatus }));
    });

    // Online/offline listeners
    const handleOnline = () => {
      setState((prev) => ({
        ...prev,
        syncStatus: { ...prev.syncStatus, isOnline: true },
      }));
    };

    const handleOffline = () => {
      setState((prev) => ({
        ...prev,
        syncStatus: { ...prev.syncStatus, isOnline: false },
      }));
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      unsubscribe();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId, practitionerId, JSON.stringify(initialAnswers)]);

  // Create or get completion
  const getOrCreateCompletion =
    useCallback(async (): Promise<OfflineCompletion> => {
      // Always check IndexedDB first to avoid race conditions with state
      let completion = await offlineDb.completions
        .filter(
          (c) =>
            c.instanceId === instanceId && c.practitionerId === practitionerId,
        )
        .first();

      if (completion) {
        // Update state if we found it in DB but not in state
        if (!state.completion) {
          setState((prev) => ({ ...prev, completion: completion ?? null }));
        }
        return completion;
      }

      // Create new completion
      completion = {
        id: generateClientId(),
        instanceId,
        nurseryId,
        practitionerId,
        startedAt: new Date().toISOString(),
        completedAt: null,
        syncState: "pending",
      };

      await offlineDb.completions.add(completion);

      // Add to sync queue
      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "completion",
        entityId: completion.id,
        priority: 1,
        createdAt: Date.now(),
        attempts: 0,
      });

      setState((prev) => ({ ...prev, completion }));

      // Trigger debounced sync if online
      if (navigator.onLine) {
        syncService.debouncedSync();
      }

      return completion;
    }, [instanceId, nurseryId, practitionerId, state.completion]);

  // Save answer
  const saveAnswer = useCallback(
    async (itemId: string, answer: AnswerValue, notes: string | null) => {
      const completion = await getOrCreateCompletion();

      // Check for existing answer
      let existingAnswer = state.answers.get(itemId);

      // Server answers (virtual, not in IndexedDB) have IDs starting with "server-"
      const isServerAnswer = existingAnswer?.id.startsWith("server-");

      if (existingAnswer && !isServerAnswer) {
        // Update existing local answer
        existingAnswer = {
          ...existingAnswer,
          answer,
          notes,
          syncState: "pending",
        };
        await offlineDb.answers.update(existingAnswer.id, {
          answer,
          notes,
          syncState: "pending",
        });
      } else {
        // Create new (either no answer exists, or it's a server answer being updated locally)
        existingAnswer = {
          id: generateClientId(),
          completionId: completion.id,
          itemId,
          answer,
          notes,
          syncState: "pending",
        };
        await offlineDb.answers.add(existingAnswer);
      }

      // Add to sync queue
      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "answer",
        entityId: existingAnswer.id,
        priority: 2,
        createdAt: Date.now(),
        attempts: 0,
      });

      setState((prev) => ({
        ...prev,
        answers: new Map(prev.answers.set(itemId, existingAnswer)),
      }));

      // Trigger debounced sync if online (allows batching rapid answers)
      if (navigator.onLine) {
        syncService.debouncedSync();
      }

      return existingAnswer;
    },
    [getOrCreateCompletion, state.answers],
  );

  // Save photo
  const savePhoto = useCallback(
    async (itemId: string, file: File): Promise<OfflinePhoto> => {
      let answer = state.answers.get(itemId);
      if (!answer) {
        throw new Error("Please answer the question first");
      }

      // Server answers (virtual, not in IndexedDB) have IDs starting with "server-"
      // We need to create a local answer first before we can attach photos
      const isServerAnswer = answer.id.startsWith("server-");
      if (isServerAnswer) {
        const completion = await getOrCreateCompletion();

        // Create a local answer based on the server answer
        const localAnswer: OfflineAnswer = {
          id: generateClientId(),
          completionId: completion.id,
          itemId,
          answer: answer.answer,
          notes: answer.notes,
          syncState: "pending",
        };

        await offlineDb.answers.add(localAnswer);

        // Add to sync queue
        await offlineDb.syncQueue.add({
          id: generateClientId(),
          type: "answer",
          entityId: localAnswer.id,
          priority: 2,
          createdAt: Date.now(),
          attempts: 0,
        });

        // Update state with the local answer
        setState((prev) => ({
          ...prev,
          answers: new Map(prev.answers.set(itemId, localAnswer)),
        }));

        answer = localAnswer;
      }

      // Convert Blob to ArrayBuffer for reliable storage on iOS/Safari
      // Blob references in IndexedDB can become invalid after app restart
      const arrayBuffer = await file.arrayBuffer();

      const photo: OfflinePhoto = {
        id: generateClientId(),
        answerId: answer.id,
        data: arrayBuffer,
        mimeType: file.type,
        fileName: file.name,
        syncState: "pending",
      };

      await offlineDb.photos.add(photo);

      // Add to sync queue
      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "photo",
        entityId: photo.id,
        priority: 3,
        createdAt: Date.now(),
        attempts: 0,
      });

      // Convert to DisplayPhoto for state (with Blob for display)
      const displayPhoto: DisplayPhoto = {
        id: photo.id,
        fileName: photo.fileName,
        mimeType: photo.mimeType,
        syncState: photo.syncState,
        blob: new Blob([photo.data], { type: photo.mimeType }),
        serverId: photo.serverId,
      };

      const currentPhotos = state.photos.get(itemId) || [];
      setState((prev) => ({
        ...prev,
        photos: new Map(prev.photos.set(itemId, [...currentPhotos, displayPhoto])),
      }));

      // Trigger debounced sync if online
      if (navigator.onLine) {
        syncService.debouncedSync();
      }

      return photo;
    },
    [state.answers, state.photos, getOrCreateCompletion],
  );

  // Delete photo
  const deletePhoto = useCallback(
    async (itemId: string, photoId: string) => {
      await offlineDb.photos.delete(photoId);

      // Remove from sync queue
      await offlineDb.syncQueue.filter((q) => q.entityId === photoId).delete();

      const currentPhotos = state.photos.get(itemId) || [];
      setState((prev) => ({
        ...prev,
        photos: new Map(
          prev.photos.set(
            itemId,
            currentPhotos.filter((p) => p.id !== photoId),
          ),
        ),
      }));
    },
    [state.photos],
  );

  // Finalize completion
  const finalizeCompletion = useCallback(async () => {
    if (!state.completion) return;

    const completedAt = new Date().toISOString();

    await offlineDb.completions.update(state.completion.id, {
      completedAt,
      syncState: "pending",
    });

    // Add finalize to sync queue
    await offlineDb.syncQueue.add({
      id: generateClientId(),
      type: "finalize",
      entityId: state.completion.id,
      priority: 4,
      createdAt: Date.now(),
      attempts: 0,
    });

    setState((prev) => ({
      ...prev,
      completion: prev.completion
        ? { ...prev.completion, completedAt, syncState: "pending" }
        : null,
    }));

    // Trigger immediate sync for finalize (no debounce - user is done)
    if (navigator.onLine) {
      syncService.immediateSync();
    }
  }, [state.completion]);

  // Manual sync trigger
  const triggerSync = useCallback(() => {
    syncService.sync();
  }, []);

  // Retry failed items
  const retryFailed = useCallback(() => {
    syncService.retryFailed();
  }, []);

  return {
    ...state,
    saveAnswer,
    savePhoto,
    deletePhoto,
    finalizeCompletion,
    triggerSync,
    retryFailed,
    getOrCreateCompletion,
  };
}
